import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const DEFAULT_RANK_COLORS = [
  "text-warning-600",
  "text-secondary-light",
  "text-danger-600",
  "text-primary-600",
  "text-primary-600",
];

/**
 * Reusable ranking table.
 *
 * Props:
 *  - title        {string}   Card header title
 *  - icon         {string}   Iconify icon name
 *  - iconColor    {string}   CSS class for the header icon color
 *  - data         {Array}    Rows: [{ talento_id, talento, promedio_orden, veces_top1 }]
 *  - loading      {boolean}
 *  - error        {any}
 *  - popovers     {{ promedio: string, top1: string }}  Tooltip text per column
 *  - rankColors   {string[]} Optional override for rank badge colors
 */
const RankingTable = ({
  title,
  icon,
  iconColor = "text-warning-600",
  data = [],
  loading,
  error,
  popovers = {},
  rankColors = DEFAULT_RANK_COLORS,
}) => {
  const [showInfo, setShowInfo] = React.useState(null);

  return (
    <div className="col-xxl-6">
      <div className="card h-100">
        <div className="card-header">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-0 fw-bold text-lg">
              <Icon icon={icon} className={`me-8 ${iconColor}`} />
              {title}
            </h6>
          </div>
        </div>

        <div className="card-body p-24">
          {loading && (
            <div className="d-flex justify-content-center align-items-center py-40">
              <span className="text-secondary-light">Cargando talentos...</span>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">Error cargando talentos</div>
          )}

          {!loading && !error && (
            <div className="table-responsive scroll-sm text-center">
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Talento</th>

                    {/* Promedio */}
                    <th scope="col" className="position-relative">
                      Promedio de posición{" "}
                      <Icon
                        icon="mdi:information-outline"
                        className="ms-4 text-primary-600"
                        width="16"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setShowInfo("promedio")}
                        onMouseLeave={() => setShowInfo(null)}
                      />
                      {showInfo === "promedio" && popovers.promedio && (
                        <div
                          className="position-absolute bg-dark text-white p-12 radius-8 shadow"
                          style={{ top: "40px", left: "0", width: "220px", fontSize: "12px", zIndex: 9999 }}
                        >
                          {popovers.promedio}
                        </div>
                      )}
                    </th>

                    {/* Top 1 */}
                    <th scope="col" className="position-relative">
                      # veces en el Top 1{" "}
                      <Icon
                        icon="mdi:information-outline"
                        className="ms-4 text-primary-600"
                        width="16"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setShowInfo("top1")}
                        onMouseLeave={() => setShowInfo(null)}
                      />
                      {showInfo === "top1" && popovers.top1 && (
                        <div
                          className="position-absolute bg-dark text-white p-12 radius-8 shadow"
                          style={{ top: "40px", left: "0", width: "220px", fontSize: "12px", zIndex: 9999 }}
                        >
                          {popovers.top1}
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {data.map((talento, index) => (
                    <tr key={talento.talento_id}>
                      <td>
                        <span className={`fw-semibold ${rankColors[index] ?? "text-secondary-light"}`}>
                          {index + 1}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <span className="text-md fw-medium text-secondary-light">
                            {talento.talento}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="text-secondary-light">
                          {talento.promedio_orden?.toFixed(1)}
                        </span>
                      </td>
                      <td>
                        <span className="text-secondary-light">
                          {talento.veces_top1}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RankingTable;
